/**
 * TABLES section (§3.2 decisions 10-12). Emission order follows the OCS
 * fixed sequence minus the tables Faz 10 owns: LTYPE → LAYER → STYLE →
 * APPID → DIMSTYLE → BLOCK_RECORD. Every table: header (handle +
 * AcDbSymbolTable) → entries (owner 330 = table handle) → ENDTAB. Layer
 * entries fill 6/62/370/390; linetype entries carry 49 dash lists;
 * DIMSTYLE records use the 105 handle code (classic quirk).
 */

import { hexToInt } from '../colors';
import type { DxfWriteContext, LayerTableEntry, LinetypeDef, TextStyleTableEntry, DimStyleTableEntry } from '../types';
import type { DxfStreamWriter } from '../DxfStreamWriter';

function emitTableShell(
    w: DxfStreamWriter, name: keyof DxfWriteContext['tableShells'],
    handle: string, count: number, emitEntries: (tableHandle: string) => void,
): void {
    w.pair(0, 'TABLE').pair(2, name).pair(5, handle).pair(330, 0);
    w.pair(100, 'AcDbSymbolTable');
    w.pair(70, count);
    emitEntries(handle);
    w.pair(0, 'ENDTAB');
}

// ----------------------------- LTYPE -----------------------------

/** DXF dash patterns for the app's six named linetypes (§3.2 decision 10):
 *  positive = draw, negative = gap, matching the OCS DASHED [20,-5] scale. */
export const LINETYPE_DEFS: LinetypeDef[] = [
    { name: 'CONTINUOUS', description: 'Solid line', dashes: [] },
    { name: 'DASHED', description: '- - - -', dashes: [20, -5] },
    { name: 'HIDDEN', description: '- - - - -', dashes: [10, -5] },
    { name: 'DOTTED', description: '. . . . .', dashes: [0, -5] },
    { name: 'CENTER', description: '___ _ ___', dashes: [50, -5, 10, -5] },
    { name: 'PHANTOM', description: '____ _ _', dashes: [50, -5, 10, -5, 10, -5] },
    { name: 'DASHDOT', description: '__ . __ .', dashes: [25, -5, 0, -5] },
];

const patternLength = (dashes: number[]): number => dashes.reduce((a, d) => a + Math.abs(d), 0);

function emitLtype(w: DxfStreamWriter, tableHandle: string, def: LinetypeDef, handle: string): void {
    w.pair(0, 'LTYPE').pair(5, handle).pair(330, tableHandle);
    w.pair(100, 'AcDbSymbolTableRecord');
    w.pair(100, 'AcDbLinetypeTableRecord');
    w.pair(2, def.name).pair(70, 0);
    w.pair(3, def.description);
    w.pair(72, 65); // A-alignment
    w.pair(73, def.dashes.length);
    w.pair(40, patternLength(def.dashes));
    def.dashes.forEach((dash) => w.pair(49, dash).pair(74, 0));
}

// ----------------------------- LAYER -----------------------------

function emitLayer(w: DxfStreamWriter, ctx: DxfWriteContext, tableHandle: string, entry: LayerTableEntry): void {
    const l = entry.layer;
    let flags = 0;
    if (l.frozen) flags |= 1;
    if (l.locked) flags |= 4;
    w.pair(0, 'LAYER').pair(5, entry.handle).pair(330, tableHandle);
    w.pair(100, 'AcDbSymbolTableRecord');
    w.pair(100, 'AcDbLayerTableRecord');
    w.pair(2, l.name).pair(70, flags);
    // Off layers carry a negative ACI; frozen layers are flagged above.
    w.pair(62, l.visible ? entry.aci : -Math.abs(entry.aci || 7));
    w.pair(6, l.linetype && ctx.ltypeEntries.has(String(l.linetype).toUpperCase()) ? String(l.linetype).toUpperCase() : 'CONTINUOUS');
    w.pair(370, typeof l.lineweight === 'number' && l.lineweight > 0 ? l.lineweight : -3); // -3 = DEFAULT
    const rgb = hexToInt(l.color);
    if (rgb !== null && ctx.version.trueColor) w.pair(420, rgb);
    w.pair(390, ctx.plotStyleNormal);
}

// ----------------------------- STYLE -----------------------------

function fontFileHint(fontFamily: string): string {
    const primary = String(fontFamily ?? '').split(',')[0]?.trim() || 'Arial';
    const family = primary.replace(/['"]/g, '').replace(/\s+/g, '').toLowerCase();
    return `${family || 'arial'}.ttf`;
}

function emitStyle(w: DxfStreamWriter, tableHandle: string, entry: TextStyleTableEntry): void {
    w.pair(0, 'STYLE').pair(5, entry.handle).pair(330, tableHandle);
    w.pair(100, 'AcDbSymbolTableRecord');
    w.pair(100, 'AcDbTextStyleTableRecord');
    w.pair(2, entry.name).pair(70, 0);
    w.pair(40, entry.height); // fixed height (0 = not fixed)
    w.pair(41, 1); // width factor
    w.pair(50, 0); // oblique
    w.pair(71, 0); // flags (no backward/upside-down/vertical)
    w.pair(42, entry.height); // last height used
    w.pair(3, entry.fontFile);
    w.pair(4, entry.bigFont);
}

// ----------------------------- APPID -----------------------------

function emitAppId(w: DxfStreamWriter, tableHandle: string, handle: string, name: string): void {
    w.pair(0, 'APPID').pair(5, handle).pair(330, tableHandle);
    w.pair(100, 'AcDbSymbolTableRecord');
    w.pair(100, 'AcDbRegAppTableRecord');
    w.pair(2, name).pair(70, 0);
}

// ----------------------------- DIMSTYLE -----------------------------

function emitDimStyle(w: DxfStreamWriter, tableHandle: string, entry: DimStyleTableEntry): void {
    w.pair(0, 'DIMSTYLE').pair(105, entry.handle).pair(330, tableHandle);
    w.pair(100, 'AcDbSymbolTableRecord');
    w.pair(100, 'AcDbDimStyleTableRecord');
    w.pair(2, entry.name).pair(70, 0);
    w.pair(3, entry.suffix); // DIMPOST
    w.pair(40, entry.scale); // DIMSCALE
    w.pair(41, entry.arrowSize); // DIMASZ
    w.pair(42, entry.extensionOffset); // DIMEXO
    w.pair(44, entry.extensionOvershoot); // DIMEXE
    w.pair(140, entry.textHeight); // DIMTXT
    w.pair(147, entry.textGap); // DIMGAP
    w.pair(176, entry.lineColorAci); // DIMCLRD
    w.pair(179, entry.textColorAci); // DIMCLRT
    w.pair(271, entry.precision); // DIMDEC
}

// ----------------------------- BLOCK_RECORD -----------------------------

function emitBlockRecord(w: DxfStreamWriter, tableHandle: string, name: string, handle: string): void {
    w.pair(0, 'BLOCK_RECORD').pair(5, handle).pair(330, tableHandle);
    w.pair(100, 'AcDbSymbolTableRecord');
    w.pair(100, 'AcDbBlockTableRecord');
    w.pair(2, name).pair(70, 0);
}

// ----------------------------- section -----------------------------

export function writeTables(w: DxfStreamWriter, ctx: DxfWriteContext): void {
    w.pair(0, 'SECTION').pair(2, 'TABLES');

    emitTableShell(w, 'LTYPE', ctx.tableShells.LTYPE, ctx.ltypeEntries.size, (tableHandle) => {
        ctx.ltypeEntries.forEach((entry) => emitLtype(w, tableHandle, entry.def, entry.handle));
    });
    emitTableShell(w, 'LAYER', ctx.tableShells.LAYER, ctx.layerEntries.size, (tableHandle) => {
        ctx.layerEntries.forEach((entry) => emitLayer(w, ctx, tableHandle, entry));
    });
    emitTableShell(w, 'STYLE', ctx.tableShells.STYLE, ctx.styleEntries.size, (tableHandle) => {
        ctx.styleEntries.forEach((entry) => emitStyle(w, tableHandle, entry));
    });
    emitTableShell(w, 'APPID', ctx.tableShells.APPID, 2, () => {
        emitAppId(w, ctx.tableShells.APPID, ctx.appIdHandles[0], 'ACAD');
        emitAppId(w, ctx.tableShells.APPID, ctx.appIdHandles[1], 'YAP');
    });
    emitTableShell(w, 'DIMSTYLE', ctx.tableShells.DIMSTYLE, ctx.dimStyleEntries.size, (tableHandle) => {
        ctx.dimStyleEntries.forEach((entry) => emitDimStyle(w, tableHandle, entry));
    });
    emitTableShell(w, 'BLOCK_RECORD', ctx.tableShells.BLOCK_RECORD, ctx.blockRecords.size, (tableHandle) => {
        ctx.blockRecords.forEach((handle, name) => emitBlockRecord(w, tableHandle, name, handle));
    });

    w.pair(0, 'ENDSEC');
}
